/**
 * WorkAreaService - Work area management with HttpService integration
 */

import httpService, { HttpResponse } from '../../../core/services/HttpService';

/**
 * Interface for work area object structure
 */
export interface WorkArea {
  id: number;
  name: string;
  description?: string;
  address?: string;
  location?: number;
  location_name?: string;
  radius_meters?: number;
  latitude?: number;
  longitude?: number;
  effective_latitude?: number;
  effective_longitude?: number;
  effective_radius?: number;
  effective_address?: string;
  employees_count?: number;
  color?: string;
  tenant?: number;
  required_qualifications?: number[];
  work_areas?: number[]; // For employee work areas
  manager?: number;
  deputy_manager?: number;
  manager_name?: string;
  deputy_manager_name?: string;
  is_active?: boolean;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for location object structure
 */
export interface Location {
  id: number;
  name: string;
  [key: string]: any;
}

/**
 * Interface for qualification object structure
 */
export interface Qualification {
  id: number;
  name: string;
  [key: string]: any;
}

/**
 * Interface for work area creation payload
 */
export interface WorkAreaCreatePayload {
  location?: number | null;
  name: string;
  description?: string;
  address?: string;
  radius_meters?: number;
  color?: string;
  tenant?: number;
  employees?: number[];
  required_qualifications?: number[];
}

/**
 * Interface for work area update payload
 */
export interface WorkAreaUpdatePayload {
  location?: number | null;
  name?: string;
  description?: string;
  address?: string;
  radius_meters?: number;
  color?: string;
  required_qualifications?: number[];
  manager?: number | null;
  deputy_manager?: number | null;
}

/**
 * Interface for employee assignment payload
 */
export interface EmployeeAssignmentPayload {
  employee_id: number;
}

/**
 * Interface for station invite payload
 */
export interface StationInvitePayload {
  work_area: number;
}

/**
 * Interface for station invite response
 */
export interface StationInviteResponse {
  id: number;
  work_area: number;
  invite_code?: string;
  expires_at?: string;
  [key: string]: any;
}

/**
 * Work Area Service
 * Centralized service for work area-related API operations using HttpService
 */
class WorkAreaService {
  /**
   * Fetch all work areas
   * @returns Promise that resolves to work areas array
   */
  static async fetchWorkAreas(includeInactive = false): Promise<WorkArea[]> {
    const response = await httpService.get<WorkArea[]>('/api/work-areas/');
    if (includeInactive) return response.data;
    return response.data.filter(wa => wa.is_active !== false);
  }

  /**
   * Fetch a single work area by ID
   * @param workAreaId - The work area ID
   * @returns Promise that resolves to the work area
   */
  static async fetchWorkArea(workAreaId: number): Promise<WorkArea> {
    const response = await httpService.get<WorkArea>(`/api/admin/work-areas/${workAreaId}/`);
    return response.data;
  }

  /**
   * Create a new work area
   * @param payload - Work area creation data
   * @returns Promise that resolves to the created work area
   */
  static async createWorkArea(payload: WorkAreaCreatePayload): Promise<WorkArea> {
    const response = await httpService.post<WorkArea>('/api/work-areas/', payload);
    return response.data;
  }

  /**
   * Update a work area
   * @param workAreaId - The work area ID
   * @param payload - Work area update data
   * @returns Promise that resolves to the updated work area
   */
  static async updateWorkArea(workAreaId: number, payload: WorkAreaUpdatePayload): Promise<WorkArea> {
    const response = await httpService.patch<WorkArea>(`/api/work-areas/${workAreaId}/`, payload);
    return response.data;
  }

  /**
   * Delete a work area
   * @param workAreaId - The work area ID
   * @returns Promise that resolves when deletion is complete
   */
  static async deleteWorkArea(workAreaId: number): Promise<void> {
    await httpService.delete(`/api/work-areas/${workAreaId}/`);
  }

  /**
   * Assign an employee to a work area
   * @param workAreaId - The work area ID
   * @param payload - Employee assignment data
   * @returns Promise that resolves when assignment is complete
   */
  static async assignEmployee(workAreaId: number, payload: EmployeeAssignmentPayload): Promise<void> {
    await httpService.post(`/api/work-areas/${workAreaId}/assign-employee/`, payload);
  }

  static async unassignEmployee(workAreaId: number, payload: EmployeeAssignmentPayload): Promise<void> {
    await httpService.post(`/api/work-areas/${workAreaId}/unassign-employee/`, payload);
  }

  /**
   * Fetch all locations for dropdowns
   * @returns Promise that resolves to locations array
   */
  static async fetchAllLocations(): Promise<Location[]> {
    const response = await httpService.get<Location[]>('/api/locations/');
    return response.data;
  }

  /**
   * Fetch all qualifications for work area requirements
   * @returns Promise that resolves to qualifications array
   */
  static async fetchAllQualifications(): Promise<Qualification[]> {
    const response = await httpService.get<Qualification[]>('/api/qualifications/');
    return response.data;
  }

  /**
   * Create a station invite for a work area
   * @param payload - Station invite data
   * @returns Promise that resolves to the station invite response
   */
  static async createStationInvite(payload: StationInvitePayload): Promise<StationInviteResponse> {
    const response = await httpService.post<StationInviteResponse>('/api/station/invite/', payload);
    return response.data;
  }

  /**
   * Update work area color
   * @param workAreaId - The work area ID
   * @param color - The new color value
   * @returns Promise that resolves to the updated work area
   */
  static async updateColor(workAreaId: number, color: string): Promise<WorkArea> {
    const response = await httpService.patch<WorkArea>(`/api/admin/work-areas/${workAreaId}/`, { color });
    return response.data;
  }
}

export default WorkAreaService; 